import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '../hooks/useIsMobile';
import { FileText, Search, BarChart2, BookOpen, UserPlus, ClipboardList, TrendingUp, ArrowRight, CheckCircle, Shield, Heart } from '../components/Icons';
import logo from '../assets/logo.png';
import StatsBar from './Landing/StatsBar';
import Features from './Landing/Features';
import HowItWorks from './Landing/HowItWorks';
import TryWithoutLogin from './Landing/TryWithoutLogin';
import CtaBand from './Landing/CtaBand';
import TeamSection from './Landing/TeamSection';

function Landing() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { isMobile, isTablet } = useResponsive();
  const isID = i18n.language === 'id';

  const features = [
    { icon: <FileText size={22} strokeWidth={1.5} />, title: isID ? 'Catat Gejala Permasalahan Pencernaan (GI)' : 'Track GI Symptoms', desc: isID ? 'Catat kondisi pencernaan anak setiap hari dengan form yang mudah dan terstruktur.' : 'Record your child\'s digestive condition daily with an easy, structured form.' },
    { icon: <Search size={22} strokeWidth={1.5} />, title: isID ? 'Tes Skrining Berbasis Penelitian Ilmiah' : 'Scientific Screening', desc: isID ? 'Tes Skrining Awal Permasalahan Pencernaan (GI), Tingkat Keparagan Gejala, serta Tingkat Rasa Nyeri (FLACC), berbasis bukti ilmiah untuk deteksi dini gangguan pencernaan.' : 'GSSS, FLACC, and evidence-based GI screening for early detection.' },
    { icon: <BarChart2 size={22} strokeWidth={1.5} />, title: isID ? 'Visualisasi Perkembangan Kondisi Pencernaan Anak' : 'Progress Visualization', desc: isID ? 'Pantau perkembangan kesehatan GI anak melalui grafik yang mudah dipahami.' : 'Monitor your child\'s GI health progress through easy-to-understand charts.' },
    { icon: <BookOpen size={22} strokeWidth={1.5} />, title: isID ? 'Pusat Informasi terkait dengan Pencernaan Anak' : 'Information Hub', desc: isID ? 'Artikel dan panduan terpercaya seputar GI dan ASD untuk mendukung perawatan anak.' : 'Trusted articles and guides about GI and ASD to support child care.' },
  ];

  const steps = [
    { icon: <UserPlus size={40} strokeWidth={1.5} />, num: '1', title: isID ? 'Buat akun gratis' : 'Create free account', desc: isID ? 'Daftar dalam 1 menit' : 'Sign up in 1 minute' },
    { icon: <ClipboardList size={40} strokeWidth={1.5} />, num: '2', title: isID ? 'Tambah profil anak' : 'Add child profile', desc: isID ? 'Input data & faktor risiko' : 'Input data & risk factors' },
    { icon: <FileText size={40} strokeWidth={1.5} />, num: '3', title: isID ? 'Mulai catat kondisi & lakukan tes skrining' : 'Start logging & testing', desc: isID ? 'Tambah catatan perkembangan pencernaan dan lakukan skrining ilmiah' : 'GI Log and scientific screening' },
    { icon: <TrendingUp size={40} strokeWidth={1.5} />, num: '4', title: isID ? 'Pantau perkembangan' : 'Monitor progress', desc: isID ? 'Grafik & laporan lengkap' : 'Charts & complete reports' },
  ];

  const trustItems = [
    { icon: <Shield size={18} strokeWidth={1.5} />, text: isID ? 'Data aman & terenkripsi' : 'Secure & encrypted data' },
    { icon: <CheckCircle size={18} strokeWidth={1.5} />, text: isID ? 'Berbasis bukti ilmiah' : 'Evidence-based' },
    { icon: <Heart size={18} strokeWidth={1.5} />, text: isID ? 'Gratis dan mudah digunakan oleh caregiver' : 'Free and easy to use for caregivers' },
  ];

  return (
    <div style={{ backgroundColor: 'var(--canvas)', minHeight: '100vh' }}>

      {/* ===== HERO ===== */}
      <section
        style={{
          backgroundColor: 'var(--canvas-soft)',
          padding: isMobile ? '54px 20px 92px' : '40px 48px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Decoration */}
        <div
          style={{
            position: 'absolute',
            inset: 0,

            background: isMobile
              ? `
                linear-gradient(
                  rgba(245,248,246,0.82),
                  rgba(245,248,246,0.82)
                ),
                radial-gradient(
                  ellipse at top right,
                  rgba(47,122,110,0.28) 30%,
                  transparent 30%
                ),
                url('https://images.unsplash.com/photo-1516627145497-ae6968895b74?q=80&w=1200&auto=format&fit=crop')
              `
              : 'radial-gradient(ellipse at right, rgba(47,122,110,0.25) 20%, transparent 70%)',

            backgroundSize: isMobile ? 'cover' : 'auto',
            backgroundPosition: isMobile ? '50%center' : 'right',
            backgroundRepeat: 'no-repeat',

            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        {/* Main Grid */}
        <div
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? '48px' : '54px',
            alignItems: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >

          {/* ================= LEFT SIDE ================= */}
          <div>

            {/* Eyebrow */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'var(--teal-light)',
                border: '1px solid var(--teal-light)',
                padding: '6px 14px',
                borderRadius: '999px',
                fontSize: isMobile ? '10px' : '12px',
                color: 'var(--teal-primary)',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                marginBottom: '32px',
              }}
            >
              {isID
                ? 'Platform Kesehatan Pencernaan Anak'
                : 'GI Health Platform for ASD'}
            </div>

            {/* Headline */}
            <h1
              style={{
                fontSize: isMobile ? '42px' : '48px',
                fontWeight: '700',
                lineHeight: '1.05',
                letterSpacing: '-0.03em',
                color: 'var(--teal-primary)',
                marginBottom: '28px',
                maxWidth: '620px',
              }}
            >
              {isID
                ? 'Pantau kesehatan pencernaan anak dengan mudah'
                : 'Monitor digestive health of children with ASD, effortlessly'}
            </h1>

            {/* Description */}
            <p
              style={{
                fontSize: isMobile ? '16px' : '14px',
                lineHeight: '1.8',
                color: 'var(--teal-primary)',
                maxWidth: '560px',
                marginBottom: '36px',
              }}
            >
              {isID
                ? 'Platform berbasis bukti ilmiah untuk caregiver anak dengan Autism Spectrum Disorder — catat, lakukan skrining, dan pantau perkembangan GI dalam satu tempat.'
                : 'An evidence-based platform for caregivers of children with ASD — log, screen, and monitor GI health in one place.'}
            </p>

            {/* Buttons */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                marginBottom: '36px',
              }}
            >
              {/* Primary */}
              <button
                onClick={() => navigate('/register')}
                style={{
                  backgroundColor: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  padding: isMobile ? '14px 20px' : '18px 32px',
                  borderRadius: '16px',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 8px 24px rgba(226,120,48,0.3)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = '#c9611a';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(226,120,48,0.45)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'var(--primary)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(226,120,48,0.35)';
                }}
              >
                {isID ? 'Mulai Gratis' : 'Get Started'}
                <ArrowRight size={24} />
              </button>

              {/* Secondary */}
              <button
                onClick={() => navigate('/public-screening')}
                style={{
                  backgroundColor: 'transparent',
                  border: '1.5px solid var(--ink-soft)',
                  padding: isMobile ? '14px 20px' : '18px 28px',
                  borderRadius: '16px',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: 'var(--ink)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'var(--canvas-soft)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Search size={22} />
                {isID ? 'Coba Tes Skrining' : 'Try Screening'}
              </button>
            </div>

            {/* Trust Items */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              
              {trustItems.map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    color: 'var(--teal-primary)',
                    fontSize: '12px',
                  }}
                >
                  {item.icon}
                  {item.text}
                </div>
              ))}
            </div>
          </div>

          {/* ================= RIGHT SIDE ================= */}
          {!isMobile && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  width: '100%',
                  maxWidth: '520px',
                  borderRadius: '28px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                  border: '3px solid rgba(255,255,255,0.4)',
                }}
              >
                <img
                  src="https://images.unsplash.com/photo-1516627145497-ae6968895b74?q=80&w=1200&auto=format&fit=crop"
                  alt="Hero"
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    objectFit: 'cover',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <StatsBar /> {/* Bar statistik di bawah hero */}  

      <Features /> {/* Bagian fitur utama */}

      <HowItWorks /> {/* Bagian cara kerja */}

      <TryWithoutLogin /> {/* Bagian coba tanpa login */}

      <CtaBand /> {/* Band CTA di akhir halaman */}

    </div>
  );
}

const styles = {
  eyebrow: {
    fontSize: '30px',
    fontWeight: '1000',
    color: 'var(--teal-primary)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  sectionHeadline: {
    fontSize: '24px',
    fontWeight: '700',
    color: 'var(--ink)',
    lineHeight: '1.2',
    letterSpacing: '-0.02em',
  },
  featureCard: {
    backgroundColor: 'var(--canvas-soft)',
    borderRadius: 'var(--radius-md)',
    padding: '24px',
    border: '1px solid var(--teal-light)',
    transition: 'box-shadow 0.2s',
  },
  iconBox: {
    width: '44px', height: '44px',
    backgroundColor: 'var(--canvas)',
    borderRadius: 'var(--radius-sm)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '16px',
    border: '1px solid var(--teal-light)',
  },
  featureTitle: {
    fontSize: '16px', fontWeight: '700',
    color: 'var(--ink)', marginBottom: '8px',
    letterSpacing: '-0.01em',
  },
  featureDesc: {
    fontSize: '14px', color: 'var(--body)',
    lineHeight: '1.6',
  },
};

export default Landing;